// ---------------------------------------------------------------
// PasswordNudge.js — Recordatorio de cambiar la contraseña.
//
// POR QUÉ EXISTE
// Hasta ahora, al dar de alta a alguien el administrador elegía su
// contraseña y se la decía de viva voz o por WhatsApp, con lo que
// seguía conociéndola indefinidamente. Las cuentas nuevas ya nacen
// con la contraseña puesta por su dueño (ver adminService.js), pero
// las que ya existían arrastran esa situación. Esto se lo recuerda
// una vez y les da el botón para cambiarla en el sitio.
//
// TRES SALIDAS, y las tres se respetan:
//   · Cambiarla ahora   → se manda el correo y no se vuelve a avisar.
//   · Recordar más tarde → vuelve a preguntar dentro de 4 días.
//   · No volver a avisar → nunca más.
//
// La decisión se guarda en la ficha de la persona (`avisoPassword`) y
// no en el navegador: si no, "no volver a avisar" duraría hasta que
// entrase desde otro móvil.
// ---------------------------------------------------------------
import { useState } from "react";
import { doc, setDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { sendPasswordResetEmail } from "firebase/auth";
import Icon from "./ui/Icon";

const DIAS_ESPERA = 4;

/**
 * ¿Hay que enseñarle el aviso a esta persona?
 * @param profile ficha de `users/{uid}`
 */
export function tocaAvisarPassword(profile) {
  if (!profile) return false;
  // Las cuentas creadas con el flujo nuevo ya tienen contraseña
  // propia: a esas no hay nada que recordarles.
  if (profile.passwordPropia) return false;

  const aviso = profile.avisoPassword;
  if (!aviso) return true; // nunca se le ha preguntado
  if (aviso.estado === "nunca" || aviso.estado === "hecho") return false;
  if (aviso.estado === "luego") {
    const hasta = aviso.hasta?.seconds ? aviso.hasta.seconds * 1000 : 0;
    return Date.now() >= hasta;
  }
  return true;
}

export default function PasswordNudge({ user, profile, onClose }) {
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState("");

  const guardar = async (estado) => {
    const datos = { estado };
    if (estado === "luego") {
      // Fecha concreta y no un contador: así no depende de cuántas
      // veces entre entre medias.
      const d = new Date();
      d.setDate(d.getDate() + DIAS_ESPERA);
      datos.hasta = d;
    }
    try {
      await setDoc(
        doc(db, "users", user.uid),
        { avisoPassword: datos },
        { merge: true }
      );
    } catch (err) {
      // Si no se puede guardar, se cierra igual: el aviso volverá,
      // que es preferible a dejar a alguien atrapado en un modal.
      console.error("No se pudo guardar la respuesta del aviso:", err);
    }
    onClose();
  };

  const cambiarAhora = async () => {
    setEnviando(true);
    setError("");
    try {
      await sendPasswordResetEmail(auth, profile?.email || user.email);
      setEnviado(true);
      // Se da por hecho al enviar el correo: desde el navegador no hay
      // forma de saber si llega a pulsar el enlace, y volver a
      // preguntar a quien ya lo ha pedido resulta pesado.
      await setDoc(
        doc(db, "users", user.uid),
        { avisoPassword: { estado: "hecho" } },
        { merge: true }
      );
    } catch (err) {
      console.error(err);
      setError("No se pudo enviar el correo. Inténtalo más tarde.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      {/* `modal` es la clase que da fondo, borde y sombra en esta web
          (ver los demás modales). `nudge-card` sólo ajusta el ancho y
          el centrado. */}
      <div className="modal nudge-card" onClick={(e) => e.stopPropagation()}>
        <div className="nudge-icono">
          <Icon name="lock" size={26} />
        </div>

        {enviado ? (
          <>
            <h2 className="nudge-titulo">Mira tu correo</h2>
            <p className="nudge-texto">
              Te hemos enviado un mensaje a{" "}
              <strong>{profile?.email || user.email}</strong> con un enlace para
              poner una contraseña nueva. Si no lo ves, revisa la carpeta de
              spam.
            </p>
            <div className="nudge-acciones">
              <button className="btn-primary" onClick={onClose}>
                Entendido
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="nudge-titulo">Cámbiate la contraseña</h2>
            <p className="nudge-texto">
              Tu contraseña la eligió el administrador al crearte la cuenta, así
              que <strong>no es sólo tuya</strong>. Te recomendamos cambiarla
              por una que no sepa nadie más: tu fichaje y tus horas son datos
              tuyos.
            </p>
            <p className="nudge-texto pequeno">
              Al pulsar el botón te llegará un correo con un enlace. Tarda menos
              de un minuto y no tienes que recordar la contraseña actual.
            </p>

            {error && <div className="admin-toast error">{error}</div>}

            <div className="nudge-acciones">
              <button
                className="btn-primary"
                onClick={cambiarAhora}
                disabled={enviando}
              >
                {enviando ? "Enviando…" : "Cambiar contraseña"}
              </button>
            </div>

            <div className="nudge-secundario">
              <button className="nudge-link" onClick={() => guardar("luego")}>
                Recordar más tarde
              </button>
              <span className="nudge-sep">·</span>
              <button className="nudge-link" onClick={() => guardar("nunca")}>
                No cambiarla y no volver a avisar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
